package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry

import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_session_expired
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.usecase.CreateRosterUseCase
import com.evoapps.evoshift.domain.usecase.ApplyWeeklySkeletonUseCase
import evoshift.composeapp.generated.resources.roster_skeleton_empty_templates
import evoshift.composeapp.generated.resources.roster_skeleton_no_week
import evoshift.composeapp.generated.resources.roster_skeleton_failed
import com.evoapps.evoshift.domain.usecase.SkeletonOutcome
import com.evoapps.evoshift.domain.usecase.CreatedRoster
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import com.evoapps.evoshift.domain.util.AppClock.now

data class CreateRosterUiState(
    val workplaceName: String,
    val isLoading: Boolean = false,
    val selectedMonth: Int = now()
        .toLocalDateTime(TimeZone.currentSystemDefault()).month.number, // Domyślnie obecny
    val selectedYear: Int = now().toLocalDateTime(TimeZone.currentSystemDefault()).year,
    val successRosterId: String? = null, // Jeśli nie null, nawiguj do detali
    val error: UiText? = null,
    /** Why the new month has no shifts from the weekly skeleton; the month opens after it is read. */
    val skeletonNotice: UiText? = null,
    /** The month just created, opened once [skeletonNotice] has been confirmed. */
    val createdRosterId: String? = null,
    /**
     * SH-194: the week has no day template for these ISO weekdays (all 7: no week at all); the
     * manager is offered to set them up before the month opens.
     */
    val skeletonPrompt: List<Int>? = null,
    /** Go to the Weekly Skeleton now (the screen navigates, then calls onSkeletonEditorOpened). */
    val openSkeletonEditor: Boolean = false,
)

class CreateRosterViewModel(
    val workplaceId: String,
    val initialWorkplaceName: String,
    private val createRosterUseCase: CreateRosterUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase, // Żeby wiedzieć kto tworzy
    /** SH-194: fills the new month from the week the manager has just set up. */
    private val applyWeeklySkeleton: ApplyWeeklySkeletonUseCase,
    /** The month to start on (SH-89: the top bar's month without a roster); else the current one. */
    initialYear: Int? = null,
    initialMonth: Int? = null
) : ViewModel() {

    private val _uiState = MutableStateFlow(
        CreateRosterUiState(workplaceName = initialWorkplaceName).let { state ->
            if (initialYear != null && initialMonth != null) state.copy(selectedYear = initialYear, selectedMonth = initialMonth) else state
        }
    )
    val uiState = _uiState.asStateFlow()

    /** SH-194: the month offered the Weekly Skeleton, while the manager is there. */
    private data class AwaitedSkeleton(val created: CreatedRoster, val year: Int, val month: Int)

    private var awaitingSkeleton: AwaitedSkeleton? = null
    private var lastCreated: CreatedRoster? = null

    fun onMonthChanged(month: Int) {
        _uiState.update { it.copy(selectedMonth = month) }
    }

    fun onYearChanged(year: Int) {
        _uiState.update { it.copy(selectedYear = year) }
    }

    fun onCreateClicked() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }

            val currentUser = getCurrentUserUseCase().first()
            if (currentUser == null) {
                _uiState.update { it.copy(isLoading = false, error = UiText.Resource(Res.string.error_session_expired)) }
                return@launch
            }

            val result = createRosterUseCase(
                workplaceId = workplaceId,
                month = _uiState.value.selectedMonth,
                year = _uiState.value.selectedYear
            )

            result.onSuccess { created ->
                AppTelemetry.log(AnalyticsEvent.RosterCreated)
                lastCreated = created
                // SH-194: days of the week without a day template: offer to set them up first.
                if (created.missingWeekdays.isNotEmpty() && created.skeletonError == null) {
                    _uiState.update {
                        it.copy(isLoading = false, skeletonPrompt = created.missingWeekdays, createdRosterId = created.rosterId)
                    }
                    return@onSuccess
                }
                val notice = skeletonNotice(created)
                _uiState.update {
                    if (notice == null) it.copy(isLoading = false, successRosterId = created.rosterId)
                    else it.copy(isLoading = false, skeletonNotice = notice, createdRosterId = created.rosterId)
                }
            }.onFailure { e ->
                _uiState.update { it.copy(isLoading = false, error = e.toUiText()) }
            }
        }
    }

    /** SH-194 "Not now": the month opens as the skeleton made it. */
    fun onSkeletonPromptDeclined() {
        _uiState.update { it.copy(skeletonPrompt = null, successRosterId = it.createdRosterId) }
    }

    /** SH-194 "Set up the week": to the Weekly Skeleton; the month is filled on the way back. */
    fun onSkeletonPromptAccepted() {
        val created = lastCreated ?: return onSkeletonPromptDeclined()
        awaitingSkeleton = AwaitedSkeleton(created, _uiState.value.selectedYear, _uiState.value.selectedMonth)
        _uiState.update { it.copy(skeletonPrompt = null, openSkeletonEditor = true) }
    }

    /** The screen has gone to the Weekly Skeleton. */
    fun onSkeletonEditorOpened() {
        _uiState.update { it.copy(openSkeletonEditor = false) }
    }

    /**
     * Back from the Weekly Skeleton (done or back): a changed week is copied into the month's days
     * that have no shifts yet, then the month opens; an unchanged one opens it as it was, without
     * asking again. Nothing happens unless the manager went there from the prompt.
     */
    fun onReturnedFromSkeletonEditor() {
        if (_uiState.value.openSkeletonEditor) return
        val awaited = awaitingSkeleton ?: return
        awaitingSkeleton = null
        val rosterId = awaited.created.rosterId
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            applyWeeklySkeleton.fillEmptyDaysIfWeekChanged(
                workplaceId, rosterId, awaited.year, awaited.month, awaited.created.weekSkeleton
            ).onSuccess { outcome ->
                val notice = when (outcome) {
                    SkeletonOutcome.EmptyTemplates -> UiText.Resource(Res.string.roster_skeleton_empty_templates)
                    else -> null
                }
                _uiState.update {
                    if (notice == null) it.copy(isLoading = false, successRosterId = rosterId)
                    else it.copy(isLoading = false, skeletonNotice = notice, createdRosterId = rosterId)
                }
            }.onFailure { e ->
                _uiState.update {
                    it.copy(isLoading = false, skeletonNotice = UiText.Resource(Res.string.roster_skeleton_failed, listOf(e.toUiText())), createdRosterId = rosterId)
                }
            }
        }
    }

    /** The manager has read why the month is empty: open it. */
    fun onSkeletonNoticeConfirmed() {
        _uiState.update { it.copy(skeletonNotice = null, successRosterId = it.createdRosterId) }
    }

    /** Null when the skeleton filled the month; otherwise what the manager should know. */
    private fun skeletonNotice(created: CreatedRoster): UiText? {
        created.skeletonError?.let { return UiText.Resource(Res.string.roster_skeleton_failed, it.toUiText()) }
        return when (created.skeleton) {
            is SkeletonOutcome.Copied -> null
            SkeletonOutcome.NoWeek -> UiText.Resource(Res.string.roster_skeleton_no_week)
            SkeletonOutcome.EmptyTemplates -> UiText.Resource(Res.string.roster_skeleton_empty_templates)
            null -> null
        }
    }
}
